import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import CompanyHeader from "./FinancialS_Components/CompanyHeader";
import EquityChangesTable, { buildRows } from "./FinancialS_Components/EquityChangesTable";
import "./EquityChangesPage.scss";

const normalizeKeys = (value) => {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nested]) => {
      acc[key.charAt(0).toLowerCase() + key.slice(1)] = normalizeKeys(nested);
      return acc;
    }, {});
  }
  return value;
};

const toNumber = (value) => Number(value ?? 0);

const normalizeSmall = (value) => (Math.abs(value) < 0.005 ? 0 : value);

const makeLine = (capital = 0, retained = 0, reserves = 0) => ({
  capital: normalizeSmall(capital),
  retained: normalizeSmall(retained),
  reserves: normalizeSmall(reserves),
  total: normalizeSmall(capital + retained + reserves),
});

function computeEquityChanges(cyBalance, pyBalance, cyIncome, periodYear) {
  const cyB = cyBalance ?? {};
  const pyB = pyBalance ?? {};
  const cyI = cyIncome ?? {};

  const movement = (key) => toNumber(cyB[key]) - toNumber(pyB[key]);

  const opening = {
    capital: toNumber(pyB.kapitaliPronarit),
    retained: toNumber(pyB.fitimiBartur),
    reserves: toNumber(pyB.rezervatTjera),
    total: toNumber(pyB.gjithsejEkuiteti),
  };

  const closing = {
    capital: toNumber(cyB.kapitaliPronarit),
    retained: toNumber(cyB.fitimiBartur),
    reserves: toNumber(cyB.rezervatTjera),
    total: toNumber(cyB.gjithsejEkuiteti),
  };

  const ownerContributions = movement("kapitaliPronarit");
  const reserveMovement = movement("rezervatTjera");
  const retainedEarningsMovement = movement("fitimiBartur");

  const netProfit = toNumber(cyI.fitimiNeto);
  const totalComprehensiveIncome = toNumber(cyI.gjithsejGjitheperfshirese ?? cyI.fitimiNeto);
  const foreignExchangeDiff = totalComprehensiveIncome - netProfit;

  const reserveAllocation = reserveMovement - foreignExchangeDiff;
  const dividends = retainedEarningsMovement - netProfit + reserveAllocation;

  const lines = {
    profitOrLoss: makeLine(0, netProfit, 0),
    foreignExchangeDiff: makeLine(0, 0, foreignExchangeDiff),
    dividends: makeLine(0, dividends, 0),
    additionalCapitalDeposits: makeLine(ownerContributions, 0, 0),
    reserveAllocation: makeLine(0, -reserveAllocation, reserveAllocation),
  };

  return {
    periodYear,
    opening,
    lines,
    closing,
  };
}

export default function EquityChangesPage() {
  const { fiscalYear } = useContext(FiscalYearContext);
  const [data, setData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);

  const formatNumber = (value) => {
    const numericValue = Number(value ?? 0);
    if (Math.abs(numericValue) < 0.005) {
      return "-";
    }

    const formatted = Math.abs(numericValue).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return numericValue < 0 ? `(${formatted})` : formatted;
  };

  const buildPdfDoc = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const periodDate = `31.12.${fiscalYear}`;
    const rows = buildRows(data?.currentYearStatement ?? {}, data?.priorYearStatement ?? {}, Number(fiscalYear));

    const bodyRows = [];
    const boldRowIndexes = new Set();
    let rowIndex = 0;

    rows.forEach((row) => {
      if (row.isSpacer) {
        bodyRows.push(["", "", "", "", ""]);
      } else {
        bodyRows.push([
          row.label || "",
          formatNumber(row.capital),
          formatNumber(row.retained),
          formatNumber(row.reserves),
          formatNumber(row.total),
        ]);
      }

      if (row.isBold) {
        boldRowIndexes.add(rowIndex);
      }
      rowIndex += 1;
    });

    doc.setTextColor(25, 25, 25);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(companyInfo?.CompanyName || "Kompania", 105, 18.5, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`Pasqyra e ndryshimeve ne ekuitet me date ${periodDate}`, 105, 23.5, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Statement of Changes in Equity", 105, 27.5, { align: "center" });

    doc.setFontSize(8);
    doc.text(`Numri fiskal: ${companyInfo?.IdentificationNumber || "-"}`, 10, 32.5);
    doc.text(`Periudha: ${periodDate}`, 200, 32.5, { align: "right" });

    autoTable(doc, {
      startY: 38,
      head: [["Pershkrimi", "Kapitali aksionar", "Fitimet e mbajtura", "Rezervat e tjera", "Gjithsej"]],
      body: bodyRows,
      theme: "grid",
      styles: {
        fontSize: 7.4,
        cellPadding: 0.95,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: [33, 33, 33],
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [0, 0, 0],
        halign: "center",
        valign: "middle",
        fontStyle: "bold",
        lineColor: [40, 73, 69],
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 32, halign: "right" },
        2: { cellWidth: 32, halign: "right" },
        3: { cellWidth: 32, halign: "right" },
        4: { cellWidth: 32, halign: "right" },
      },
      didParseCell: (hookData) => {
        if (hookData.section !== "body") {
          return;
        }

        const currentRowIndex = hookData.row.index;
        const sourceRow = rows[currentRowIndex];

        if (sourceRow?.isSpacer) {
          hookData.cell.styles.fillColor = [255, 255, 255];
          hookData.cell.styles.lineWidth = 0;
          hookData.cell.styles.minCellHeight = 3;
          hookData.cell.text = [""];
        }

        if (boldRowIndexes.has(currentRowIndex)) {
          hookData.cell.styles.fillColor = [245, 249, 247];
          hookData.cell.styles.fontStyle = "bold";
        }
      },
      margin: { top: 8, right: 6, bottom: 8, left: 6 },
      pageBreak: "auto",
    });

    let signatureY = (doc.lastAutoTable?.finalY || 40) + 20;
    const pageHeight = doc.internal.pageSize.getHeight();

    if (signatureY > pageHeight - 22) {
      doc.addPage();
      signatureY = 36;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text("Pergatiti", 42, signatureY - 7, { align: "center" });
    doc.text("Miratoi", 148, signatureY - 7, { align: "center" });
    doc.line(20, signatureY, 85, signatureY);
    doc.line(115, signatureY, 180, signatureY);

    const totalPages = doc.internal.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Faqe ${page}/${totalPages}`, 6, 292);
      doc.text("Gjeneruar nga Accountree", 200, 292, { align: "right" });
    }

    const companySlug = (companyInfo?.CompanyName || "Kompania")
      .trim()
      .replace(/\s+/g, "_");

    return { doc, companySlug };
  };

  const handleDownloadPdf = async () => {
    if (isGeneratingPdf || isLoading || !data) {
      return;
    }
    setIsGeneratingPdf(true);
    try {
      const { doc, companySlug } = buildPdfDoc();
      doc.save(`Pasqyra_e_ndryshimeve_ne_ekuitet_${fiscalYear}_${companySlug}.pdf`);
    } catch (pdfError) {
      console.error("Gabim gjatë gjenerimit të PDF:", pdfError);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintPdf = async () => {
    if (isGeneratingPrint || isLoading || !data) {
      return;
    }
    setIsGeneratingPrint(true);
    try {
      const { doc } = buildPdfDoc();
      const blobUrl = doc.output("bloburl");

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      };
    } catch (printError) {
      console.error("Gabim gjatë printimit:", printError);
    } finally {
      setIsGeneratingPrint(false);
    }
  };

  useEffect(() => {
    const fetchEquityChanges = async () => {
      if (!fiscalYear || fiscalYear <= 0) {
        setError("Viti fiskal nuk është përcaktuar saktë.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const headers = authHeader();
        const year = Number(fiscalYear);

        const [bsNow, isNow, bsPrev, isPrev, companyRes] = await Promise.all([
          axios.get(ApiLink.API_URL + "BalanceSheet/getBalanceSheet", {
            headers,
            params: { fiscalYear: year },
          }),
          axios.get(ApiLink.API_URL + "BalanceSheet/getIncomeStatement", {
            headers,
            params: { fiscalYear: year },
          }),
          axios.get(ApiLink.API_URL + "BalanceSheet/getBalanceSheet", {
            headers,
            params: { fiscalYear: year - 1 },
          }),
          axios.get(ApiLink.API_URL + "BalanceSheet/getIncomeStatement", {
            headers,
            params: { fiscalYear: year - 1 },
          }),
          axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers }),
        ]);

        if (Array.isArray(companyRes.data) && companyRes.data.length > 0) {
          setCompanyInfo(companyRes.data[0]);
        } else {
          setCompanyInfo(null);
        }

        const normalizedBsNow = normalizeKeys(bsNow.data);
        const normalizedIsNow = normalizeKeys(isNow.data);
        const normalizedBsPrev = normalizeKeys(bsPrev.data);
        const normalizedIsPrev = normalizeKeys(isPrev.data);

        const currentYearStatement = computeEquityChanges(
          normalizedBsNow?.currentYear,
          normalizedBsNow?.priorYear,
          normalizedIsNow?.currentYear,
          year
        );

        const priorYearStatement = computeEquityChanges(
          normalizedBsPrev?.currentYear,
          normalizedBsPrev?.priorYear,
          normalizedIsPrev?.currentYear,
          year - 1
        );

        setData({ currentYearStatement, priorYearStatement });
      } catch (fetchError) {
        console.error("Gabim në marrjen e pasqyrës së ndryshimeve në ekuitet:", fetchError);
        setError(
          fetchError.response?.data?.message || "Gabim në marrjen e të dhënave"
        );
        setCompanyInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEquityChanges();
  }, [fiscalYear]);

  if (error) {
    return (
      <div className="EQ__Page__Container">
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            color: "#856404",
          }}
        >
          <h3>Gabim</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="EQ__Page__Container">
      <div className="EQ__Page__HeaderData">
        <CompanyHeader
          reportTitle={`Pasqyra e ndryshimeve në ekuitet për vitin që përfundon më 31 dhjetor ${fiscalYear}`}
          reportSubtitle="Statement of Changes in Equity"
          fiscalYear={fiscalYear}
          onDownloadPdf={handleDownloadPdf}
          isGeneratingPdf={isGeneratingPdf}
          onPrint={handlePrintPdf}
          isGeneratingPrint={isGeneratingPrint}
        />
      </div>

      <hr className="EQ__Page__Divider" />

      <div className="EQ__Page__TableWrap">
        <EquityChangesTable
          currentYearData={data?.currentYearStatement}
          priorYearData={data?.priorYearStatement}
          fiscalYear={Number(fiscalYear)}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
